/**
 * 팝업 본문의 `**강조**` 토큰화 — 줄바꿈은 그대로 두고 굵게만 가른다.
 *
 * ⚠️ **별 모듈에 두는 것이 설계다.** 앱의 api 모듈(`lib/popups.ts`)에 두면 그 모듈을 `vi.mock`하는
 * spec에서 이 순수 함수까지 사라진다(vitest factory mock은 부분 mock이 아니라 팩토리가 돌려준
 * 키만 존재한다) — 같은 사고가 `lib/organizations`에 술어를 더해 그 모듈을 mock하는 spec 17건이
 * 전멸한 전례로 있다(`memory/feedback-pure-predicate-must-not-live-in-a-vi-mocked-module`).
 * 그 규율은 `packages/ui`로 올라온 뒤에도 유효하다 — 여기 두면 **mock될 모듈이 아예 아니다.**
 *
 * ⚠️ 마크다운 전체를 해석하지 **않는다.** 본문은 소비자(전사 공지·업데이트 내역)가 만드는
 * 사용자용 문구이고, 거기 필요한 것은 「같은 날 추가분을 굵게」 하나다(결정 2026-10-14 ⓐ —
 * 「전체를 다시 띄우고 추가분 강조」). 링크·이미지·HTML을 해석하면 **작성 권한이 곧 XSS 경로**가
 * 되므로, 토큰화 결과를 텍스트 노드로만 렌더한다(`dangerouslySetInnerHTML` 금지).
 */
export interface PopupTextSegment {
  text: string;
  strong: boolean;
}

/**
 * `**…**`를 굵은 구간으로 가른다.
 *
 * - 쌍이 맞지 않는 `**`는 **그대로 글자로** 남긴다(작성자가 별표를 쓴 것일 수 있다).
 * - 빈 강조(`****`)는 구간을 만들지 않는다.
 * - 강조 안의 줄바꿈도 허용한다 — 본문이 여러 줄인 공지가 흔하다.
 */
export function splitEmphasis(body: string): PopupTextSegment[] {
  const segments: PopupTextSegment[] = [];
  let rest = body;

  while (rest.length > 0) {
    const open = rest.indexOf('**');
    if (open === -1) {
      segments.push({ text: rest, strong: false });
      break;
    }

    const close = rest.indexOf('**', open + 2);
    if (close === -1) {
      // 닫는 쌍이 없다 — 남은 전부를 평문으로(여기서 끊어 버리면 글자가 사라진다)
      segments.push({ text: rest, strong: false });
      break;
    }

    if (open > 0) segments.push({ text: rest.slice(0, open), strong: false });
    const inner = rest.slice(open + 2, close);
    if (inner.length > 0) segments.push({ text: inner, strong: true });
    rest = rest.slice(close + 2);
  }

  return segments.filter((s) => s.text.length > 0);
}

/**
 * 강조 구간의 클래스 — **굵게 + 강조색**.
 *
 * pcon ADR-023 §4가 `**강조**`를 「굵게·**강조색**」으로 정의하는데 종전 web 렌더는
 * `font-semibold`뿐이라 **색이 없었다**(2026-10-26 사용자 요구: 「새로 추가된 항목을 붉게」).
 *
 * ⚠️ **`rose-600`은 리터럴 hex가 아니라 이 패키지가 정한 세만틱 색 규약이다.** preset JSDoc
 * (`packages/ui/tailwind-preset.js`)이 「목업이 이름 붙인 축(brand, ink, surface, line)은 별칭
 * 토큰으로 등록하고 **세만틱 색(blue/emerald/amber/rose/violet 600계)만 raw 팔레트를 그대로
 * 쓴다**」고 정한다. 같은 패키지의 선례가 그것이다 — `TextButton`의 `danger` 톤과 `Badge`의
 * `danger` 톤이 둘 다 `text-rose-600`이다. ⇒ preset에 `emphasis` 토큰을 새로 더하는 쪽은 그
 * 문단이 정한 축을 벗어나므로 **기각했다**(축을 늘리려면 그 JSDoc을 먼저 고쳐야 한다).
 *
 * ⚠️ raw 팔레트를 쓰는 덕에 **앱이 preset을 빠뜨려도 이 색은 생성된다**(Tailwind 기본 팔레트).
 * 반면 본문 바탕색(`text-ink-700`)은 별칭 토큰이라 preset 등록이 **필수**다 — 두 앱 모두
 * `presets: [uiPreset]` + `content`에 `packages/ui/src/**`가 있다(2026-10-26 확인).
 *
 * ⚠️ ⭐ **이 자리의 `rose`는 danger가 «아니라» 「새것」을 뜻한다 — 같은 값을 다른 뜻으로 쓴다.**
 * 이 패키지의 선례 둘은 그 값을 **파괴적 액션**에 쓴다(`TextButton`의 `danger` 톤 ·
 * `Button`의 `danger` 톤). `Badge`는 톤 이름이 색 이름 그대로(`rose`)지만 그 주석이 심각도
 * 4단계(CRITICAL/HIGH/MEDIUM/LOW)를 설명하므로 거기서도 **가장 높은 심각도** 쪽이다. 즉 같은
 * 화면에 뱃지·삭제 버튼이 함께 있으면 **「새로 추가된 항목」이 위험 신호로 읽힐 수 있다.**
 * 색 자체는 사용자 동사(「붉게」)가 정한 것이라 그대로 두되, **톤 체계를 정리하는 사람이 이
 * 자리를 알아보도록** 적어 둔다 — 여기를 `danger`로 분류하지 말 것이고, 세만틱 톤 이름을
 * 도입한다면 이 자리의 이름은 `danger`가 아니라 **`new`·`emphasis`** 쪽이다.
 */
export const POPUP_EMPHASIS_CLASS = 'font-semibold text-rose-600';
